import { motion } from "framer-motion";
import { PlayCircle, BadgeCheck, ArrowRight, Lock, Clock } from "lucide-react";
import { Reveal } from "@/components/Reveal";

// Pricing + payment section for any course that carries `payment_plans` in
// its seed data. Renders the demo, the full-price reference, and each
// payable option — buttons only take real payment once the backend reports
// Razorpay is configured; until then they fall back to Enquire, exactly like
// every other integration on this site degrades gracefully when unset.
// `configured` and `onSelectPlan` come from the parent (Landing), which owns
// the single canonical /payments/plans fetch and the shared PaymentDialog —
// so the demo card and these plan cards open the exact same dialog instance.
export default function PaymentPlans({ course, demo, configured, onEnquire, onDemoEnquire, onSelectPlan, testId = "payment-plans" }) {
  const plans = course?.payment_plans;
  if (!plans) return null;

  const openPlan = (plan) => {
    if (!configured) {
      onEnquire && onEnquire();
      return;
    }
    onSelectPlan(plan);
  };

  return (
    <div data-testid={testId} className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6 items-stretch">
      {demo && (
        <Reveal>
          <div className="h-full rounded-3xl border-2 border-terracotta/30 bg-gradient-to-br from-terracotta/[0.06] via-amber-500/[0.06] to-lemon/[0.06] p-6 flex flex-col">
            <span className="inline-flex items-center gap-1.5 self-start rounded-full bg-terracotta text-white px-3 py-1.5 font-sans text-[11px] font-black uppercase tracking-wide">
              <PlayCircle size={12} fill="currentColor" /> Try it first
            </span>
            <h3 className="font-display font-black mt-4 text-2xl text-ink leading-tight">{demo.fee}</h3>
            <p className="font-sans text-xs font-bold text-graphite uppercase tracking-wide mt-1">Live demo session</p>
            {demo.duration && (
              <span className="inline-flex items-center gap-1 mt-2 self-start rounded-full bg-ink text-white px-2.5 py-1 font-sans text-[10px] font-black uppercase tracking-wide">
                <Clock size={10} /> {demo.duration}
              </span>
            )}
            <p className="text-graphite text-[14px] mt-3 leading-relaxed flex-1">
              Build a tiny working app live with prompting — no commitment, then decide.
            </p>
            <motion.button
              type="button"
              onClick={onDemoEnquire || onEnquire}
              whileHover={{ y: -2 }}
              data-testid={`${testId}-demo-cta`}
              className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-ink text-white font-sans font-bold px-5 py-3 shadow-md hover:shadow-xl transition-shadow"
            >
              {configured ? (
                <>
                  <Lock size={14} /> Pay {demo.fee} to book
                </>
              ) : (
                <>
                  Book a demo · {demo.fee} <ArrowRight size={15} />
                </>
              )}
            </motion.button>
          </div>
        </Reveal>
      )}

      {(plans.options || []).map((plan, i) => (
        <Reveal key={plan.key} delay={0.06 * (i + 1)}>
          <div className="h-full rounded-3xl border-2 border-line hover:border-emerald/50 transition-colors bg-surface p-6 flex flex-col shadow-sm relative overflow-hidden">
            {plan.badge && (
              <span className="inline-flex self-start items-center gap-1.5 rounded-full bg-emerald/10 text-emerald px-3 py-1.5 font-sans text-[11px] font-black uppercase tracking-wide">
                <BadgeCheck size={12} /> {plan.badge}
              </span>
            )}
            <div className="flex items-end gap-2 mt-4 flex-wrap">
              <h3 className="font-display font-black text-3xl text-ink leading-tight">{plan.amount_display}</h3>
              <span className="font-sans text-xs font-semibold text-graphite mb-1">pay today</span>
            </div>
            <p className="font-sans text-xs font-bold text-graphite uppercase tracking-wide mt-1">{plan.label}</p>
            {plan.key === "full_upfront" && plans.full_price_display && (
              <p className="font-sans text-[12.5px] text-emerald font-semibold mt-1">
                instead of {plans.full_price_display} — full course fee, nothing else to pay
              </p>
            )}
            {plan.total_amount_display && (
              <p className="font-sans text-[12.5px] text-graphite mt-1">
                {plan.total_amount_display} in total, split across {plan.installments_total} payments
              </p>
            )}
            <p className="text-graphite text-[14px] mt-3 leading-relaxed flex-1">{plan.note}</p>
            <motion.button
              type="button"
              onClick={() => openPlan(plan)}
              whileHover={{ y: -2 }}
              data-testid={`${testId}-cta-${plan.key}`}
              className="mt-5 inline-flex items-center justify-center gap-2 rounded-full bg-emerald text-white font-sans font-bold px-5 py-3 shadow-md hover:bg-emeraldhover hover:shadow-xl transition-all"
            >
              {configured ? (
                <>
                  <Lock size={14} /> Pay {plan.amount_display}
                </>
              ) : (
                <>
                  Enquire — {plan.amount_display} <ArrowRight size={15} />
                </>
              )}
            </motion.button>
          </div>
        </Reveal>
      ))}
    </div>
  );
}
